import React from "react";
import { useTranslation } from "react-i18next";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useNavigate } from "react-router-dom";
import { ggpsLoginModes } from "src/namePurchase/data-access/ggpsLoginModes";
import useGetGgpsLogin from "src/shared/data-access/ggpsLogin/useGetGgpsLogin";
import { useGgpsNew } from "src/welcome/data-access/useGgpsNew";
import { OAuthModes } from "src/welcome/types";

export default function RegisterModal({
  open,
  setOpen,
  setOpenLoginModal,
  setSelectedUser,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenLoginModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedUser: React.Dispatch<React.SetStateAction<number | null>>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate, isLoading: isLoadingGgpsLogin } = useGetGgpsLogin();
  const { mutate: mutateNew, isLoading: isLoadingGgpsLoginNew } = useGgpsNew();

  const signupCards = [
    {
      title: t("Απευθείας Εγγραφή στο Σύστημα"),
      image: <AppRegistrationIcon sx={{ color: "#003476" }} />,
      description: t("Για Μη Υπόχρεους Γ.Ε.ΜΗ"),
      onNavigate: () => navigate("/welcomeNonGemi/nonGemiRegistrationForm"),
    },
    {
      title: t("Εγγραφή με διασταύρωση ΑΦΜ μέσω ΓΓΠΣ"),
      image: <HowToRegIcon sx={{ color: "#003476" }} />,
      description: t("Για Υπόχρεους Γ.Ε.ΜΗ"),
      onNavigate: () => {
        import.meta.env.VITE_OAUTH2_ACTIVATED === "true"
          ? mutateNew({ mode: OAuthModes.LOGIN_REGISTRATION })
          : mutate({ mode: ggpsLoginModes.REGISTER });
      },
    },
  ];

  const handleRedirectToLogin = () => {
    setOpen(false);
    setOpenLoginModal(true);
    setSelectedUser(0);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        sx={{ borderRadius: "1rem" }}
      >
        <DialogTitle
          variant="h6"
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">{t("Επιλογή Τύπου Εγγραφής")}</Typography>
          <IconButton onClick={() => setOpen(false)}>
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            alignItems: "center",
            mt: "1rem",
            pb: 5,
          }}
        >
          {signupCards.map((card, index) => (
            <Box
              key={index}
              sx={{
                border: "1px solid #f0f0f2",
                minHeight: "5rem",
                width: "100%",
                borderRadius: ".5rem",
                display: "flex",
                alignItems: "center",
                p: 1,
                px: 2,
                gap: 2,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#f4f4f5",
                },
              }}
              onClick={card.onNavigate}
            >
              {(isLoadingGgpsLogin || isLoadingGgpsLoginNew) && index == 1 ? (
                <CircularProgress
                  sx={{
                    color: "grey",
                    justifySelf: "center",
                  }}
                />
              ) : (
                <>
                  {card.image}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.1rem",
                    }}
                  >
                    <Typography>{card.title}</Typography>
                    <Typography variant="subtitle2" sx={{ color: "#9599a4" }}>
                      {card.description}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          ))}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              mt: ".5em",
            }}
          >
            <Typography
              sx={{
                textAlign: "center",
                fontSize: ".9em",
                color: "#71717a",
              }}
              variant="subtitle1"
            >
              {t("Έχετε ήδη λογαριασμό; Συνδεθείτε")}
            </Typography>
            <Button
              sx={{
                color: "black",
                backgroundColor: "transparent",
                fontWeight: 500,
                fontSize: ".9em",
                p: 0,
                minWidth: "auto",
                border: "none",
                ml: ".2em",
                "&:hover": {
                  textDecoration: "underline",
                  backgroundColor: "transparent",
                },
              }}
              onClick={handleRedirectToLogin}
            >
              {t("εδώ")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
